import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { createFamilyFormStepper, familyFormStepper } from '@pages/hubs/Family/const/family-form-stepper';
import { FamilySelectOptionsBelowEnum } from '@pages/hubs/Family/enum/family-select-options-below-enum';
import { FamilyStepsEnum } from '@pages/hubs/Family/enum/family-steps-enum';
import { isListOver } from '@shared/functions/isListOver';
import { changeCoverPhoto } from '@shared/utils/hubs/changeCoverPhoto';

import {
  createFamilyBodyArt,
  deleteFamilyBodyArt,
  getFamilyBodyArts,
  getFamilyList,
  updateFamilyBodyArt,
} from './family-thunk';

import type { RootFamilyData, RootFamilyItemsDataStep } from './family-types';
import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { GetHubsParams } from '@pages/hubs/Pets/store/pets-types';
import type { AssignPeopleSelectValueModel } from '@shared/models/assignPeopleSelectValue.model';
import type { FilterParams } from '@shared/models/hubs/hub-filters.model';
import type { FormStepper } from '@shared/models/hubs/hub-form-stepper.model';
import type { MediaType } from '@shared/models/media.model';
import type { BodyArtModel, BodyArtsMetaCustom } from '@shared/models/profileNew/profileNew.model';

const familyInitialState = {
  isLoadingFamily: false,
  isListOver: false as boolean | number,
  isEmptyFamilyDataWithFilters: false,
  currentFamily: {} as RootFamilyData,
  isEmptyFamilyWithFilter: false,

  bodyArts: null as BodyArtModel[] | null,
  isLoadingBodyArts: false,
  bodyArtsMeta: {
    page: 1,
    isStopPagination: false,
  } as BodyArtsMetaCustom,

  familyEditMode: false,

  familyTreeMembers: [] as any[],
  family: {
    data: [] as RootFamilyData[],
    meta: {} as RootMeta,
  },
  share: {
    sharePeopleList: [] as AssignPeopleSelectValueModel[],
    selectUser: null as AssignPeopleSelectValueModel | null,
  },

  filterParams: {
    query: '',
    shared_filter: [],
  } as FilterParams,
  createFamily: {
    formStepper: familyFormStepper as FormStepper<any>,
    currentStep: FamilyStepsEnum.preStepFamilyType,
    familyType: FamilySelectOptionsBelowEnum.child,
    draftItem: null as RootFamilyData | null,
    isCanChangeCurrentStep: true,
  },
};
type InitialStateType = typeof familyInitialState;

const familySlice = createSlice({
  name: 'familySlice',
  initialState: familyInitialState as InitialStateType,
  reducers: {
    // ------------------ common ------------------
    setLoadingFamily(state, action: PayloadAction<{ isLoading: boolean }>) {
      state.isLoadingFamily = action.payload.isLoading;
    },
    clearFamilyFilters(state) {
      state.filterParams = { ...state.filterParams, query: '' };
    },
    // ------------------ main page ------------------
    setFamilyFilterParams(state, action: PayloadAction<{ params: FilterParams }>) {
      state.filterParams = action.payload.params;
    },
    setFamilyDataWithNewPosition(state, action: PayloadAction<{ newPositionData: RootFamilyData[] }>) {
      state.family.data = action.payload.newPositionData;
    },
    clearFamilyState(state) {
      state.family = familyInitialState.family;
    },
    isEmptyDataFamilyWithFilter(
      state,
      action: PayloadAction<{
        filters: Pick<GetHubsParams, 'shared_filter' | 'query'>;
        familyData: RootFamilyData[];
      }>,
    ) {
      const { shared_filter, query } = action.payload.filters;
      state.isEmptyFamilyDataWithFilters =
        (!!shared_filter?.length || !!query) && !action.payload.familyData.length;
    },
    removeCurrentFamily(state, action: PayloadAction<{ familyId: number }>) {
      const index = state.family.data.findIndex(
        (familyMember) => familyMember.id === action.payload.familyId,
      );
      if (index !== -1) state.family.data.splice(index, 1);

      if (
        !state.family.data.length &&
        (!!state.filterParams.shared_filter.length || !!state.filterParams.query)
      ) {
        state.isEmptyFamilyWithFilter = true;
      }
    },

    // ------------------ view family ------------------
    setEditMode(state, action: PayloadAction<{ editMode: boolean }>) {
      state.familyEditMode = action.payload.editMode;
    },
    setCurrentFamily(state, action: PayloadAction<{ family: RootFamilyData }>) {
      state.currentFamily = action.payload.family;
    },
    // ------------------ create family ------------------

    setFamilyCreateType: (state, action: PayloadAction<any>) => {
      state.createFamily.familyType = action.payload;
    },
    changeFamilyCreateFormStepperPassedDisableSteps: (
      state,
      action: PayloadAction<{
        enabledSteps: FamilyStepsEnum[];
        passedStep: RootFamilyItemsDataStep[];
      }>,
    ) => {
      const enabledSteps = new Set(action.payload.enabledSteps);

      action.payload.passedStep.forEach(({ step }) => {
        const currentStep = state.createFamily.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });
    },
    // ------------------ gallery family ------------------
    addGalleryPhoto(state, action: PayloadAction<{ newGalleryPhotos: MediaType[] }>) {
      state.currentFamily.photos = action.payload.newGalleryPhotos;
    },
    updateGalleryCoverPhoto(state, action: PayloadAction<{ coverPhoto: MediaType }>) {
      state.currentFamily.cover_photo = action.payload.coverPhoto;
    },
    removeGalleryCoverPhoto(
      state,
      action: PayloadAction<{ deleteCoverID: number; isCover: boolean | undefined }>,
    ) {
      const deleteCoverIndex = state.currentFamily.photos.findIndex(
        (photo) => photo.id === action.payload.deleteCoverID,
      );
      if (deleteCoverIndex !== -1) state.currentFamily.photos.splice(deleteCoverIndex, 1);
      if (!!state.currentFamily.photos.length) {
        const { photos } = changeCoverPhoto(state.currentFamily.photos);
        const hasCoverPhoto = state.currentFamily.photos.filter((photo) => photo.is_cover);

        state.currentFamily.photos = [...photos];
        if (!hasCoverPhoto.length) {
          state.currentFamily.cover_photo = photos.length ? photos[photos.length - 1] : null;
        }
      } else state.currentFamily.cover_photo = null;
    },
    // ------------------ files family ------------------
    addDocuments(state, action: PayloadAction<{ newDocuments: MediaType[] }>) {
      state.currentFamily.documents = action.payload.newDocuments;
    },
    // ------------------ family tree step ---------------
    setFamilyTreeMember: (state, action: PayloadAction<{ user: any }>) => {
      state.familyTreeMembers.push(action.payload.user);
    },
    // setFamilyCreateWay: (state, action: PayloadAction<any>) => {
    //   state.createPet.wayToEnterPetInfo = action.payload;
    // },
    setFamilyCoverPhoto: (state, action: PayloadAction<{ cover_photo: MediaType | null }>) => {
      if (state.createFamily.draftItem) {
        state.createFamily.draftItem.cover_photo = action.payload.cover_photo;
      }
    },
    setFamilyCreateDraftItem: (state, action: PayloadAction<any | null>) => {
      state.createFamily.draftItem = action.payload;
      if (action.payload) {
        state.createFamily.familyType = action.payload.is_adult
          ? FamilySelectOptionsBelowEnum.member
          : FamilySelectOptionsBelowEnum.child;
      }
    },
    updateFamilyDraftItem: (state, action: PayloadAction<any | null>) => {
      state.createFamily.draftItem = { ...state.createFamily.draftItem, ...action.payload };
    },
    setFamilyCreateCurrentStep: (state, action: PayloadAction<any>) => {
      state.createFamily.currentStep = action.payload;
      state.createFamily.isCanChangeCurrentStep = true;
    },
    changeFamilyCreateFormStepperFromDraftItem: (state, action: PayloadAction<any[]>) => {
      const enabledSteps = new Set([FamilyStepsEnum.preStepFamilyType]);
      //   const isIncludedAiStep = action.payload.some((item) => item.step === CreatePetStepsEnum.preStepAI);

      //  state.createPet.wayToEnterPetInfo = isIncludedAiStep ? WayToPetInfo.ai : WayToPetInfo.manual;

      action.payload.forEach(({ step }) => {
        const currentStep = state.createFamily.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });

      const lastStepInPayload = action.payload[action.payload.length - 1];
      if (lastStepInPayload) {
        state.createFamily.currentStep =
          createFamilyFormStepper[
            createFamilyFormStepper.findIndex((item) => item === lastStepInPayload.step) + 1
          ];
      }
    },
    setFamilyCreateFormStepPassed: (state, action: PayloadAction<{ step: any; isPassed?: boolean }>) => {
      const { step, isPassed = true } = action.payload;
      if (state.createFamily.formStepper[step]) {
        state.createFamily.formStepper[step].isPassed = isPassed;
      }
    },
    setFamilyCreateFormStepDisabled: (state, action: PayloadAction<{ step: any; isDisabled?: boolean }>) => {
      const { step, isDisabled = true } = action.payload;
      if (state.createFamily.formStepper[step]) {
        state.createFamily.formStepper[step].isDisabled = isDisabled;
      }
    },
    setFamilyCreateFormStepSkip: (state, action: PayloadAction<{ step: any; isSkip?: boolean }>) => {
      const { step, isSkip = true } = action.payload;
      if (state.createFamily.formStepper[step]) {
        state.createFamily.formStepper[step].isSkip = isSkip;
      }
    },
    setFamilyCreateCanChangeCurrentStep: (state, action: PayloadAction<boolean>) => {
      state.createFamily.isCanChangeCurrentStep = action.payload;
    },

    resetCreateStore: (state) => {
      state.createFamily = familyInitialState.createFamily;
    },
    resetFamilyDataStore: () => {
      return familyInitialState;
    },
  },
  extraReducers: ({ addCase }) => {
    // ------------------ main page ------------------
    addCase(getFamilyList.fulfilled, (state, { payload }) => {
      if (payload.meta.current_page === 1) {
        state.family.data = payload.data;
        state.family.meta = payload.meta;
        state.isListOver = isListOver(payload);
      } else {
        state.family.data.push(...payload.data);
        state.family.meta = payload.meta;
        state.isListOver = isListOver(payload);
      }
    });
    addCase(getFamilyBodyArts.fulfilled, (state, { payload }) => {
      const newFilteredData = payload.data.filter(
        (item) => !state.bodyArts?.some((bodyArt) => bodyArt.id === item.id),
      );

      state.bodyArts = state.bodyArts ? [...state.bodyArts, ...newFilteredData] : payload.data;
      state.isLoadingBodyArts = true;

      state.bodyArtsMeta = {
        page: payload.meta.current_page + 1,
        isStopPagination: payload.meta.last_page <= state.bodyArtsMeta.page,
      };
    });
    addCase(createFamilyBodyArt.fulfilled, (state, { payload }) => {
      if (state.bodyArts) {
        state.bodyArts.unshift(payload);
      }
    });
    addCase(updateFamilyBodyArt.fulfilled, (state, { payload }) => {
      state.bodyArts = state.bodyArts?.length
        ? state.bodyArts.map((item: BodyArtModel) => {
            if (item.id === payload.id) {
              return payload;
            }
            return item;
          })
        : null;
    });
    addCase(deleteFamilyBodyArt.fulfilled, (state, { payload }) => {
      state.bodyArts = state.bodyArts?.length
        ? state.bodyArts.filter((item: any) => item.id !== payload)
        : null;
    });
  },
});

export const {
  setLoadingFamily,
  changeFamilyCreateFormStepperPassedDisableSteps,
  setFamilyCreateCanChangeCurrentStep,
  setFamilyCreateFormStepDisabled,
  setFamilyCreateFormStepSkip,
  changeFamilyCreateFormStepperFromDraftItem,
  setFamilyCreateDraftItem,
  setFamilyCreateFormStepPassed,
  setFamilyCreateCurrentStep,
  setFamilyCreateType,
  updateFamilyDraftItem,
  setFamilyCoverPhoto,
  resetCreateStore,
  setFamilyTreeMember,
  clearFamilyFilters,
  setFamilyFilterParams,
  setFamilyDataWithNewPosition,
  clearFamilyState,
  setEditMode,
  addGalleryPhoto,
  updateGalleryCoverPhoto,
  removeGalleryCoverPhoto,
  addDocuments,
  isEmptyDataFamilyWithFilter,
  removeCurrentFamily,
  setCurrentFamily,
  resetFamilyDataStore,
} = familySlice.actions;
export default familySlice.reducer;
